import type { IconDefinition } from '@fortawesome/fontawesome-svg-core'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import clsx from 'clsx'
import type { ReactElement } from 'react'
import { aiShimmer } from '~/components/Global/Elements/AiShimmer/AiShimmer.css'
import { Tooltip } from '~/components/Global/Elements/Tooltip/Tooltip'
import {
  buttonExtraSmall,
  container,
  containerBase,
  containerBaseExtraSmall,
  itemActive,
  itemBase,
  itemTheme
} from './SegmentedControl.css'

type SegmentedOption = {
  value: string
  icon: IconDefinition
  tooltip?: string
  variant?: 'ai'
}

type SegmentedControlProps = {
  options: [SegmentedOption, SegmentedOption]
  value: string
  onChange: (value: string) => void
  mode?: 'primary' | 'secondary'
  buttonSize?: 'extraSmall' | 'small'
}

export const SegmentedControl = ({
  options,
  value,
  onChange,
  mode = 'primary',
  buttonSize = 'extraSmall'
}: SegmentedControlProps): ReactElement => {
  return (
    <div
      className={clsx(
        containerBase,
        buttonSize === 'extraSmall' ? containerBaseExtraSmall : null,
        container[mode]
      )}
    >
      {options.map((option) => {
        const isActive = option.value === value
        const button = (
          <button
            key={option.value}
            type="button"
            className={clsx(
              itemBase,
              buttonSize === 'extraSmall' ? buttonExtraSmall : null,
              !(isActive && option.variant === 'ai') && itemTheme[mode],
              isActive &&
                (option.variant === 'ai' ? aiShimmer : itemActive[mode])
            )}
            onClick={() => onChange(option.value)}
            aria-label={option.tooltip ?? option.value}
          >
            <FontAwesomeIcon icon={option.icon} />
          </button>
        )

        if (option.tooltip) {
          return (
            <Tooltip
              key={option.value}
              asChild
              text={option.tooltip}
              direction="top"
            >
              {button}
            </Tooltip>
          )
        }

        return button
      })}
    </div>
  )
}
